// TAG PASE movements: CSV parsing and the traslado each run of crossings
// probably belongs to. Proposals are only suggestions — nothing is written
// until someone accepts one (pase_link RPC). See
// supabase/migrations/20261019_pase_movements.sql.

// pase_tags row -> "Tag 1 · IMDM 28552048-9", as printed on the PASE card.
export const tagLabel = (t) =>
  t ? `${t.name} · ${t.tag.replace(/^([A-Z]+)(\d+)$/, "$1 $2")}${t.check_digit ? `-${t.check_digit}` : ""}` : "";

export const tagOptions = (tags, emptyLabel = "Sin TAG") => [
  { label: emptyLabel, value: "" },
  ...tags.map((t) => ({ label: tagLabel(t), value: t.tag })),
];

// ------------------------------------------------------------------ parsing
// apps.pase.com.mx exports one CSV per tag. Post-paid tags have a "Tarjeta"
// column, the prepaid one "Saldo Disponible" (+ RECARGA / COMISION rows).
// Accented letters come as literal octal escapes ("BUCER\315AS").

const unescapeOctal = (s) => s.replace(/\\([0-7]{3})/g, (_, o) => String.fromCharCode(parseInt(o, 8)));

const splitCsvLine = (line) => {
  const out = [];
  let cur = "";
  let q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (q) {
      if (c === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (c === '"') q = false;
      else cur += c;
    } else if (c === '"') q = true;
    else if (c === ",") {
      out.push(cur);
      cur = "";
    } else cur += c;
  }
  out.push(cur);
  return out.map((s) => s.trim());
};

const nullish = (s) => (!s || s.toLowerCase() === "null" ? null : s);

// "10/10/2026" + "07:01:59" (or "21:08") -> ISO in Mexico City time (UTC-6, no DST).
const toIso = (date, time) => {
  const [d, m, y] = date.split("/");
  const [hh, mm, ss = "00"] = (time || "00:00").split(":");
  const pad = (n) => String(n).padStart(2, "0");
  return `${y}-${pad(m)}-${pad(d)}T${pad(hh)}:${pad(mm)}:${pad(ss)}-06:00`;
};
const toDate = (date) => {
  if (!date) return null;
  const [d, m, y] = date.split("/");
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
};

const kindOf = (concepto) => {
  const c = concepto.toUpperCase();
  if (c.includes("PEAJE")) return "toll";
  if (c.includes("COMISION")) return "fee"; // "COMISION POR RECARGA"
  if (c.includes("RECARGA")) return "topup";
  return "other";
};

export function parsePaseCsv(text) {
  const lines = unescapeOctal(text).split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const head = splitCsvLine(lines[0]);
  const col = (name) => head.findIndex((h) => h.toLowerCase() === name.toLowerCase());
  const iTag = col("Tag");
  if (iTag < 0 || col("Folio") < 0 || col("Importe") < 0) {
    throw new Error("No parece una exportación de movimientos PASE (faltan Tag / Folio / Importe)");
  }
  const iFecha = col("Fecha de Movimiento");
  const iHora = col("Hora de Movimiento");
  const iFechaHora = col("Fecha/Hora de Movimiento");
  return lines.slice(1).map((line) => {
    const v = splitCsvLine(line);
    const get = (name) => {
      const i = col(name);
      return i < 0 ? null : nullish(v[i]);
    };
    let date;
    let time;
    if (iFecha >= 0) {
      date = v[iFecha];
      time = iHora >= 0 ? v[iHora] : null;
    } else {
      [date, time] = (v[iFechaHora] || "").split(/\s+/);
    }
    const concepto = get("Concepto") || "";
    const saldo = get("Saldo Disponible");
    return {
      tag: v[iTag],
      folio: get("Folio") || "0",
      moved_at: toIso(date, time),
      charged_on: toDate(get("Fecha Cobro")),
      concepto,
      kind: kindOf(concepto),
      caseta_no: get("No. Caseta"),
      caseta: get("Caseta"),
      carril: get("Carril") || get("No. Carril"),
      clase: get("Clase"),
      amount: Math.abs(Number(get("Importe")) || 0),
      balance: saldo == null ? null : Number(saldo),
    };
  });
}

// ---------------------------------------------------------------- proposals
// A run = one tag's consecutive pending crossings with less than RUN_GAP_H
// hours between them (a driver may stop to sleep, so this is generous).
const RUN_GAP_H = 10;
// Candidate traslados: crossings may fall this far outside started/finished
// (times are typed in by hand, often rounded).
const LOOSE_BEFORE_H = 3;
const LOOSE_AFTER_H = 6;
// Scoring uses a tight window: the first toll comes after departure.
const STRICT_BEFORE_H = 0.5;
const STRICT_AFTER_H = 1.5;
// A traslado with no finished_at is assumed to take at most this long.
const OPEN_TRIP_H = 72;

const H = 3600 * 1000;
const t = (iso) => new Date(iso).getTime();

export function groupRuns(movements) {
  const byTag = {};
  movements
    .filter((m) => m.kind === "toll")
    .slice()
    .sort((a, b) => t(a.moved_at) - t(b.moved_at))
    .forEach((m) => (byTag[m.tag] = byTag[m.tag] || []).push(m));
  const runs = [];
  Object.entries(byTag).forEach(([tag, list]) => {
    let cur = null;
    list.forEach((m) => {
      if (cur && t(m.moved_at) - t(cur.end) <= RUN_GAP_H * H) {
        cur.movements.push(m);
        cur.end = m.moved_at;
      } else {
        cur = { tag, start: m.moved_at, end: m.moved_at, movements: [m] };
        runs.push(cur);
      }
    });
  });
  runs.forEach((r) => {
    r.key = `${r.tag}-${r.start}`;
    r.total = r.movements.reduce((s, m) => s + Number(m.amount), 0);
  });
  return runs.sort((a, b) => t(a.start) - t(b.start));
}

const tripWindow = (o, before, after) => {
  const start = o.started_at || o.scheduled_at;
  if (!start) return null;
  const end = o.finished_at ? t(o.finished_at) : t(start) + OPEN_TRIP_H * H;
  return [t(start) - before * H, end + after * H];
};
const fitIn = (run, w) =>
  w ? run.movements.filter((m) => t(m.moved_at) >= w[0] && t(m.moved_at) <= w[1]).length / run.movements.length : 0;

export const destKey = (label) =>
  (label || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

// Score each run against every traslado it could belong to:
//   - share of crossings inside the traslado's real times (tight window),
//   - booths already seen on traslados to the same destination (routes),
//   - the tag's usual trasladista (main driver; backup counts less),
//   - first toll within 6 h of departure,
//   - a traslado already carrying this tag (+) or another tag (−),
//   - the TAG marked on the traslado (pase_tag): strongest signal either way.
// knownDrivers: { tag: Set(trasladista) } and linkedTags: { orderId: Set(tag) }
// from tolls already linked; routes: { destKey: Set(caseta_no) }.
export function proposeRuns(runs, orders, knownDrivers = {}, linkedTags = {}, routes = {}) {
  const strict = (o) => tripWindow(o, STRICT_BEFORE_H, STRICT_AFTER_H);
  // Also learn a tag's driver from runs that fit exactly one traslado, so two
  // cars on the same route can be told apart on a first upload too.
  const tagDrivers = Object.fromEntries(Object.entries(knownDrivers).map(([k, v]) => [k, new Set(v)]));
  orders.forEach((o) => {
    if (o.pase_tag && o.trasladista != null) (tagDrivers[o.pase_tag] = tagDrivers[o.pase_tag] || new Set()).add(o.trasladista);
  });
  runs.forEach((run) => {
    const fits = orders.filter((o) => fitIn(run, strict(o)) === 1);
    if (fits.length !== 1 || fits[0].trasladista == null) return;
    (tagDrivers[run.tag] = tagDrivers[run.tag] || new Set()).add(fits[0].trasladista);
  });

  const scored = runs.map((run) => {
    const booths = run.movements.map((m) => m.caseta_no);
    const candidates = orders
      .map((o) => {
        if (fitIn(run, tripWindow(o, LOOSE_BEFORE_H, LOOSE_AFTER_H)) < 0.8) return null;
        const fit = fitIn(run, strict(o));
        const known = routes[destKey(o.dest_label)];
        const route = known?.size ? booths.filter((b) => known.has(b)).length / booths.length : 0;
        const drivers = tagDrivers[run.tag];
        const driverMatch = !!drivers && drivers.has(o.trasladista);
        const backupMatch = !!drivers && o.trasladista_secondary != null && drivers.has(o.trasladista_secondary);
        const lead = (t(run.start) - t(o.started_at || o.scheduled_at)) / H;
        const sameTag = (linkedTags[o.id] || new Set()).has(run.tag);
        const otherTag = [...(linkedTags[o.id] || [])].some((tg) => tg !== run.tag);
        const declared = o.pase_tag ? (o.pase_tag === run.tag ? 1 : -1) : 0;
        const score =
          3 * declared +
          fit +
          1.5 * route +
          (driverMatch ? 1 : backupMatch ? 0.3 : 0) +
          (lead >= 0 && lead <= 6 ? 0.2 : 0) +
          (sameTag ? 1 : 0) -
          (otherTag ? 2 : 0);
        return { order: o, fit, route, driverMatch, sameTag, declared, score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score);
    return { ...run, candidates };
  });

  // Most confident runs pick first; one tag per traslado.
  const taken = {};
  scored
    .slice()
    .sort((a, b) => (b.candidates[0]?.score || 0) - (a.candidates[0]?.score || 0))
    .forEach((run) => {
      const free = run.candidates.filter((c) => !taken[c.order.id] || taken[c.order.id] === run.tag);
      const [pick, next] = free;
      run.proposed = pick ? pick.order : null;
      run.why = !pick
        ? "Ningún traslado registrado cubre estas fechas"
        : [
            pick.declared === 1 ? "el traslado tiene marcado este TAG" : null,
            pick.declared === -1 ? "ojo: el traslado tiene marcado otro TAG" : null,
            pick.fit === 1
              ? "dentro del horario del traslado"
              : `${Math.round(pick.fit * 100)}% dentro del horario del traslado`,
            pick.route >= 0.5 ? "mismas casetas que traslados a ese destino" : null,
            pick.sameTag ? "ya tiene casetas de este TAG" : null,
            pick.driverMatch ? "el trasladista usa este TAG" : null,
            next && pick.score - next.score < 0.15 ? `revisa: #${next.order.id} encaja igual de bien` : null,
          ]
            .filter(Boolean)
            .join(" · ");
      if (pick) taken[pick.order.id] = run.tag;
    });
  return scored;
}

// ---------------------------------------------------------------- warnings
// history: every toll movement known (any status), to learn usual prices.
export function movementWarnings(run, history, cashTickets = []) {
  const usual = {};
  history.forEach((m) => {
    if (m.kind !== "toll") return;
    const k = `${m.caseta_no}|${m.clase}`;
    (usual[k] = usual[k] || {})[m.amount] = ((usual[k] || {})[m.amount] || 0) + 1;
  });
  const out = {};
  run.movements.forEach((m, i) => {
    const w = [];
    const counts = usual[`${m.caseta_no}|${m.clase}`] || {};
    const [mode, n] = Object.entries(counts).sort((a, b) => b[1] - a[1])[0] || [];
    if (mode != null && n >= 2 && Number(mode) !== Number(m.amount)) {
      w.push(`Normalmente cobra $${Number(mode).toLocaleString("es-MX")}`);
    }
    const dup = run.movements.find(
      (x, j) => j !== i && x.caseta_no === m.caseta_no && Math.abs(t(x.moved_at) - t(m.moved_at)) < 12 * H,
    );
    if (dup) w.push("Cobrada dos veces en menos de 12 h");
    const day = new Date(t(m.moved_at) - 6 * H).toISOString().slice(0, 10);
    if (cashTickets.some((c) => Number(c.total) === Number(m.amount) && c.ticket_date === day)) {
      w.push("El trasladista también subió un ticket en efectivo por este monto ese día");
    }
    if (w.length) out[m.id] = w;
  });
  return out;
}
